import { Box, TextButton, Tooltip, useOpenModal } from '@modusign/design-system';
import { useSigners } from '@modusign/react-signing';
import { useUIState } from '@modusign/react-signing/setup';
import XLSX from '@sheet/edit';
import { ChangeEvent, createRef, MouseEvent, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import { TextFieldFontLoader } from '@app/components/TextField';
import ExcelUploadErrorModal from '@app/containers/BulkRequestExcelUploadButton/ExcelUploadErrorModal';
import BulkRequestStartPageButton from '@app/features/bulkRequest/components/BulkRequestStartPageButton';
import { ERROR_TOO_MUCH_ROWS } from '@app/features/bulkRequest/constant';
import useBulkRequestItem from '@app/features/bulkRequest/hooks/useBulkRequestItem';
import { useBulkRequestPermission } from '@app/features/bulkRequest/hooks/useBulkRequestPermission';
import { usePDF } from '@app/hooks/usePDF';
import { numberToColumnName } from '@app/libs/excel';
import { createFormValues } from '@app/libs/excel/bulkRequest';
import {
  ExcelValidationError,
  validateFormValues,
} from '@app/libs/excel/bulkRequest/validateFormValue';
import getExtensionFromFileName from '@app/libs/file/getExtensionFromFileName';
import PDFContainer from '@app/libs/reactPdfJs/containers/PDFContainer';
import { getColumnList, getRequesterInputTextColumns } from '@app/store/bulkRequest/form/state';
import { putRequestTasks } from '@app/store/bulkRequest/requestTasks/tasks/actions';
import { getIsFetching, getIsUpdating } from '@app/store/bulkRequest/requestTasks/tasks/selectors';
import { modalClose, modalOpen } from '@app/store/modal/actions';
import FormValue from '@app/store/transport-layer/bulk-request/bulk-requests/request-tasks/FormValue';

import RequesterInputValidator from './RequesterInputValidator';

import './BulkRequestExcelUploadButton.scss';

interface BulkRequestExcelUploadButtonProps {
  bulkRequestId: string;
  isTextButton?: boolean;
  isModal: boolean;
  text: string;
}

const defaultProps = {
  isModal: false,
};

const isSpreadSheetStyleParsingError = (message: string) =>
  message.includes('DXF') || message.includes('StyleElement');

const ACCEPT_FILETYPE = ['xlsx'];

/*
 * TODO(@highcastlee): 모달 redux 의존 제거하고 DS 모달로 개선하기
 */
function BulkRequestExcelUploadButton({
  bulkRequestId,
  isTextButton,
  isModal,
  text,
}: BulkRequestExcelUploadButtonProps) {
  const dispatch = useDispatch();
  const fileInputRef = createRef<HTMLInputElement>();
  const openModal = useOpenModal();

  const { editablePermission } = useBulkRequestPermission({ bulkRequestId });

  const { signers } = useSigners();
  const { ui } = useUIState();

  const [excelValues, setExcelValues] = useState<FormValue[]>([]);
  const [excelErrors, setExcelErrors] = useState<ExcelValidationError[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const form = useSelector(getColumnList);
  const textColumns = useSelector(getRequesterInputTextColumns);
  const isUpdating = useSelector(getIsUpdating);
  const isFetching = useSelector(getIsFetching);
  const othersContactInfosInTemplate = useMemo(() => {
    return signers
      .map((signer) => signer.contact)
      .filter((contact): contact is string => !!contact);
  }, [signers]);

  const { pdf } = usePDF();

  const { isLoading: isLoadingBulkRequestItem } = useBulkRequestItem(bulkRequestId);

  function handleExcelUpload(event: MouseEvent) {
    event.preventDefault();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }

  function handleExcelLoadError(errorMessage: string) {
    setIsValidating(false);
    if (isModal) {
      dispatch(modalClose());
    }
    openModal(({ isOpen, onClose }) => (
      <ExcelUploadErrorModal errorMessage={errorMessage} isOpen={isOpen} onClose={onClose} />
    ));
  }

  function handleFinishExcelValidate({
    errors,
    formValues,
  }: {
    errors: ExcelValidationError[];
    formValues: FormValue[];
  }) {
    if (isModal) {
      dispatch(modalClose());
    }
    if (errors.length > 0) {
      const modalErrorMessages = errors.map((error: ExcelValidationError) => {
        const columnName = numberToColumnName(error.column);
        const formColumn = form[error.column];
        return {
          columnName: `${columnName}${error.row}`,
          type: formColumn?.title,
          message: error.message,
        };
      });

      dispatch(
        modalOpen('BULK_REQUEST_EXCEL_VALIDATION_ERROR_MODAL', {
          bulkRequestId,
          errors: modalErrorMessages,
        })
      );
    } else {
      dispatch(putRequestTasks({ bulkRequestId, formValues }));
    }
    setExcelValues([]);
  }

  function handleDrop(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target?.files?.[0];
    if (!file) {
      handleExcelLoadError('파일이 선택되지 않았습니다.');
      return;
    }
    const extension = getExtensionFromFileName(file.name).slice(1);

    if (ACCEPT_FILETYPE.indexOf(extension) === -1) {
      if (isModal) {
        dispatch(modalClose());
      }

      openModal(({ isOpen, onClose }) => (
        <ExcelUploadErrorModal
          errorMessage="대량전송은 엑셀파일(xlsx)만 업로드할 수 있습니다."
          isOpen={isOpen}
          onClose={onClose}
        />
      ));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        if (e && e.target) {
          const { result } = e.target;
          const data = new Uint8Array(result as ArrayBuffer);
          const workBook = XLSX.read(data, { type: 'array' });

          const formValues = createFormValues({
            form,
            workBook,
          });

          const excelValidationErrors = validateFormValues({
            form,
            formValues,
            othersContactInfosInTemplate,
            isSigningInOrder: !!ui.isInOrder,
          });

          if (textColumns.length > 0) {
            /**
             * 요청자 입력 텍스트 필드의 '넘침' 검사는 화면에 렌더링해서
             * 영역 넘침을 검사해야합니다.
             * 뷰 영역의 state를 갱신해서 요청자 텍스트 필드의 '넘침'
             * 유효성 검사를 RequesterInputValidator 컴포넌트에서 진행합니다.
             */
            setExcelValues(formValues);
            setExcelErrors(excelValidationErrors);
            setIsValidating(true);
          } else {
            /**
             * 요청자 입력 텍스트 필드가 없는 경우 바로 검사를 종료합니다.
             */
            handleFinishExcelValidate({ errors: excelValidationErrors, formValues });
          }
        }
      } catch (error) {
        setIsValidating(false);

        if (error.message === ERROR_TOO_MUCH_ROWS) {
          if (isModal) {
            dispatch(modalClose());
          }

          openModal(({ isOpen, onClose }) => (
            <ExcelUploadErrorModal
              errorMessage="엑셀 파일의 전송 건 수가 너무 많습니다.\n5,000건 이하로 업로드해 주세요."
              isOpen={isOpen}
              onClose={onClose}
            />
          ));

          return;
        }

        if (isSpreadSheetStyleParsingError(error.message)) {
          openModal(({ isOpen, onClose }) => (
            <ExcelUploadErrorModal
              errorMessage={
                <Box as="ol" textAlign="left">
                  <li>1. 사용하는 프로그램에서 대량전송 파일 열기</li>
                  <li>2. 작성한 내용 전체 복사</li>
                  <li>3. 구글 스프레드 시트에 붙여넣기 </li>
                  <li>4. Microsoft Excel 형식으로 다운로드</li>
                  <li>5. 모두싸인 대량전송에서 엑셀 업로드</li>
                </Box>
              }
              guide="엑셀 외 프로그램으로 파일을 수정할 경우 업로드할 수 없습니다.
            엑셀이 없는 경우, 아래 순서로 진행해 주세요."
              isOpen={isOpen}
              onClose={onClose}
            />
          ));

          return;
        }

        handleExcelLoadError(error.message);
      }
    };
    reader.onerror = () => {
      handleExcelLoadError('엑셀 파일을 읽을 수 없습니다.');
    };
    reader.readAsArrayBuffer(file);

    /**
     * File input 값을 초기화합니다.
     * 초기화하지 않으면 onChnage 이벤트가 한번 발생 후 다시
     * 발생하지 않을 수 있습니다.
     *
     * 참고
     * https://stackoverflow.com/questions/19643265/second-use-of-input-file-doesnt-trigger-onchange-anymore
     * https://stackoverflow.com/questions/12030686/html-input-file-selection-event-not-firing-upon-selecting-the-same-file
     */
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  }

  return (
    <>
      {isUpdating && (
        <GlobalLoadingSpinner
          message="엑셀 파일을 검사하는 중..."
          overLayClassName="Overlay--active"
        />
      )}
      {(isFetching || isLoadingBulkRequestItem) && (
        <GlobalLoadingSpinner overLayClassName="Overlay--active" />
      )}
      <Tooltip
        content={editablePermission?.message}
        disabled={editablePermission?.isPermitted}
        placement="right"
        scale="sm"
      >
        {isTextButton ? (
          <TextButton disabled={!editablePermission?.isPermitted} onClick={handleExcelUpload}>
            {text}
          </TextButton>
        ) : (
          <BulkRequestStartPageButton
            disabled={!editablePermission?.isPermitted}
            onClick={handleExcelUpload}
          >
            {text}
          </BulkRequestStartPageButton>
        )}
      </Tooltip>

      <input
        accept=".xlsx"
        className="hidden"
        ref={fileInputRef}
        type="file"
        onChange={handleDrop}
      />
      {isValidating && (
        <RequesterInputValidator
          excelValues={excelValues}
          onValidateEnd={(requesterInputTextErrors: ExcelValidationError[]) => {
            setIsValidating(false);

            handleFinishExcelValidate({
              errors: excelErrors.concat(requesterInputTextErrors),
              formValues: excelValues,
            });
          }}
        />
      )}
      {!isModal && (
        /**
         * 요청자 텍스트 필드의 영역 넘침 검사를 위해 PDFContainer를 화면 밖에 렌더링 합니다.
         * 이는 사용자가 볼 수 없고 레이아웃에 포함되지 않습니다.
         * PDFContainer는 PDF를 읽어오고 로딩이 완료되면
         * react-pdf.js/store/reducers/pageViewportSizes.ts
         * 리듀서에 각 페이지별 viewport(페이지의 가로, 세로 사이즈)를 저장합니다.
         *
         * 요청자 필드는 PDF 페이지 크기에 상대적인 위치, 크기를 가지므로
         * PDFContainer에서 PDF 로딩 후 얻은 페이지별 pageViewportSizes를
         * 활용하여 요청자 필드의 영역을 RequesterInputValidator에서 렌더링하고,
         * 영역 넘침을 검사합니다.
         */
        <div className="BulkRequestExcelUploadButton__pdf">
          <TextFieldFontLoader />
          {pdf && (
            <PDFContainer
              src={pdf}
              thumbnailContainerClassName="hidden"
              useThumbnailViewer={false}
            />
          )}
        </div>
      )}
    </>
  );
}

BulkRequestExcelUploadButton.defaultProps = defaultProps;

export default BulkRequestExcelUploadButton;
